import { Injectable } from "@angular/core";
import {
  ApiHistorie,
  ApiHistorieBijlage,
  ApiHistorieDetails,
  ApiHistorieExamenonderdeel,
  ApiHistorieItem,
  ApiHistorieMinor,
  ApiHistorieMinorExamenonderdeel,
  ApiHistorieProfilering,
  ApiHistoriePuntenPerCategorie,
} from "@app/core/api/model/api-historie";
import {
  ProposalHistory,
  ProposalHistoryCategoryDetails,
  ProposalHistoryCourse,
  ProposalHistoryDetails,
  ProposalHistoryDocument,
  ProposalHistoryElectives,
  ProposalHistoryItem,
  ProposalHistoryMinor,
  ProposalHistoryMinorExamComponent,
  ProposalHistoryOtherExamComponent,
} from "@app/core/domain/proposal-history";
import { ApiCursusInVoorstel } from "../api/model/api-indienen-voorstel";

@Injectable({
  providedIn: "root",
})
export class ProposalHistoryMappingService {
  mapProposalHistory(apiHistory: ApiHistorie): ProposalHistory {
    return {
      items: apiHistory.items.map((item) => this.mapProposalHistoryItem(item)) ?? [],
    };
  }

  private mapProposalHistoryItem(item: ApiHistorieItem): ProposalHistoryItem {
    return {
      splaId: item.spla_id,
      hsplId: item.hspl_id,
      studentNr: item.studentnummer,
      reviewMomentDescription: item.beoordelingsmoment_omschrijving,
      statusDescription: item.status_omschrijving,
      decisionDate: item.datum_besluit,
    };
  }

  mapProposalHistoryDetails(apiHistorieDetails: ApiHistorieDetails): ProposalHistoryDetails {
    return {
      splaId: apiHistorieDetails.spla_id,
      hsplId: apiHistorieDetails.hspl_id,
      studentNr: apiHistorieDetails.studentnummer,
      reviewMomentDescription: apiHistorieDetails.beoordelingsmoment_oms,
      submitDate: apiHistorieDetails.datum_indienen,
      studentExplanation: apiHistorieDetails.toelichting_indienen,
      adviceExplanation: apiHistorieDetails.toelichting_advies,
      advisor: apiHistorieDetails.titel_advies,
      decisionDate: apiHistorieDetails.datum_besluit,
      decisionExplanation: apiHistorieDetails.toelichting_besluit,
      decisionMaker: apiHistorieDetails.titel_besluit,
      attachments: apiHistorieDetails.bijlagen.map((attachment) => this.mapProposalHistoryDocument(attachment)) ?? [],
      electives: this.mapProposalHistoryElectives(apiHistorieDetails.profilering),
      otherExamComponents:
        apiHistorieDetails.overige_examenonderdelen.map((component) =>
          this.mapProposalHistoryOtherExamComponent(component),
        ) ?? [],
    };
  }

  private mapProposalHistoryOtherExamComponent(
    apiHistorieExamenonderdeel: ApiHistorieExamenonderdeel,
  ): ProposalHistoryOtherExamComponent {
    return {
      code: apiHistorieExamenonderdeel.examenonderdeel,
      name: apiHistorieExamenonderdeel.examenonderdeel_naam,
      specialisationName: apiHistorieExamenonderdeel.specialisatie_naam,
      courses: apiHistorieExamenonderdeel.cursussen.map((course) => this.mapProposalHistoryCourse(course)),
    };
  }

  private mapProposalHistoryElectives(apiHistorieProfilering: ApiHistorieProfilering): ProposalHistoryElectives {
    return {
      examComponentName: apiHistorieProfilering.naam,
      minimumPoints: apiHistorieProfilering.minimum_punten,
      pointsSelected: apiHistorieProfilering.punten_geselecteerd,
      pointsPerCategory: apiHistorieProfilering.punten_per_categorie.map((category) =>
        this.mapProposalHistoryCategoryDetails(category),
      ),
      individualArrangements: apiHistorieProfilering.individuele_regelingen.map((arrangement) =>
        this.mapProposalHistoryCourse(arrangement),
      ),
      courses: apiHistorieProfilering.losse_cursussen.map((course) => this.mapProposalHistoryCourse(course)),
      minors: apiHistorieProfilering.minors.map((minor) => this.mapMinor(minor)),
    };
  }

  private mapMinor(apiMinor: ApiHistorieMinor): ProposalHistoryMinor {
    return {
      code: apiMinor.minor,
      name: apiMinor.naam,
      type: apiMinor.type,
      examComponents: apiMinor.examenonderdelen.map((comp) => this.mapMinorExamComponent(comp)),
      studyProgram: apiMinor.minor_examenprogramma,
      minimumPoints: apiMinor.minimum_punten,
    };
  }

  private mapMinorExamComponent(
    apiMinorExamenonderdeel: ApiHistorieMinorExamenonderdeel,
  ): ProposalHistoryMinorExamComponent {
    const minorExamComponent: ProposalHistoryMinorExamComponent = {
      id: apiMinorExamenonderdeel.sple_id,
      name: apiMinorExamenonderdeel.minor_onderdeel_naam,
      courses: apiMinorExamenonderdeel.cursussen.map((cursus) => this.mapProposalHistoryCourse(cursus)),
    };
    return minorExamComponent;
  }

  private mapProposalHistoryCategoryDetails(category: ApiHistoriePuntenPerCategorie): ProposalHistoryCategoryDetails {
    return {
      categoryDescription: category.categorie_oms,
      pointsSelected: category.punten_geselecteerd,
    };
  }

  private mapProposalHistoryCourse(apiCursusInVoorstel: ApiCursusInVoorstel): ProposalHistoryCourse {
    return {
      id: apiCursusInVoorstel.spcu_id,
      code: apiCursusInVoorstel.cursus,
      name: apiCursusInVoorstel.korte_naam,
      customCourseName: apiCursusInVoorstel.eigen_cursus,
      customCoursePoints: apiCursusInVoorstel.eigen_cursus_punten,
      labelColor: apiCursusInVoorstel.kleur,
      studyPoints: apiCursusInVoorstel.studiepunten,
      categoryCode: apiCursusInVoorstel.categorie ?? "",
      categoryDescription: apiCursusInVoorstel.categorie_oms ?? "",
    };
  }

  private mapProposalHistoryDocument(attachment: ApiHistorieBijlage): ProposalHistoryDocument {
    return {
      documentId: attachment.id_document,
      fileName: attachment.bestandsnaam,
    };
  }
}
